import React, { useRef, useState } from "react";
import HeroBanner from "components/Marketing/Components/HeroBanner";
import PageTypePicker from "components/PageTypePicker";
import SearchBar from "components/SearchBar";
import { usePagePickerContext } from "utils/context/builder/page";
import cloneDeep from "lodash/cloneDeep";
import startCase from "lodash/startCase";
import {
  TEMPLATE_BLANK_PAGE_UUID,
  TEMPLATE_CATEGORY
} from "utils/constants/template";
import classnames from "classnames";
import HoverIconButton from "components/HoverIconButton";
import { BUTTON_TAGS } from "utils/constants/ui";
import { interpolateRouteWithParams } from "utils/route";
import PATHS from "utils/paths";
import { useCtxReferences } from "utils/selectors";
import { SECTIONS } from "components/FormerEditor/utils";
import Footer from "components/Footer";
import { clearStorePageBuilder } from "utils/builder";
import { fetchAppPagesTemplatesIndex } from "actions/app";
import CardDetailLoader from "components/CardDetailLoader";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

const PageTypePickerContainer = ({ actions }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { values, setValues } = usePagePickerContext();

  const resetValues = () => {
    setValues({ categories: {}, search: "" });
    if (inputRef && inputRef.current && inputRef.current.value) {
      inputRef.current.value = "";
    }
  };

  const { application } = useCtxReferences();

  const blankDestination = application
    ? interpolateRouteWithParams(
        PATHS.BUILDER_APPLICATIONS_PAGES_SHOW_SECTION,
        {
          application,
          page: {
            uuid: TEMPLATE_BLANK_PAGE_UUID
          },
          section: {
            uuid: SECTIONS.PAGE_BUILDER
          }
        }
      )
    : "";

  const fetchTemplates = () => {
    setLoading(true);
    actions.fetchAppPagesTemplatesIndex(() => setLoading(false));
  };

  const search = values.search;
  const categories = values.categories;

  const toggleCategory = (category) => {
    const updatedValues = cloneDeep(values);
    if (updatedValues.categories[category]) {
      delete updatedValues.categories[category];
    } else {
      updatedValues.categories[category] = true;
    }
    setValues(updatedValues);
  };
  const clearCategories = () => {
    const updatedValues = cloneDeep(values);
    updatedValues.categories = {};

    setValues(updatedValues);
  };
  const hasActiveCategories =
    categories && Object.values(categories).length > 0;

  const categoryItems = Object.values(TEMPLATE_CATEGORY).reduce(
    (memo, category, categoryIx) => {
      if (category !== "scratch") {
        const isActive = Boolean(categories && categories[category]);
        memo.push(
          <li
            key={category}
            onClick={() => toggleCategory(category)}
            className={classnames("pb1 items-center flex", {
              ml2: categoryIx - 1
            })}
          >
            <div
              className={classnames("fw5 pa1 br2 ba bg-white--hover pointer", {
                "b--primary color-primary": isActive,
                "hairline-1": !isActive
              })}
            >
              {startCase(category.replace(/_/g, " "))}
            </div>
          </li>
        );
      }
      return memo;
    },
    []
  );
  if (hasActiveCategories) {
    categoryItems.push(
      <li
        key="clear"
        onClick={clearCategories}
        className="pb1 flex ml2 pl2 hairline-1 bl"
      >
        <HoverIconButton
          type="close"
          onClick={clearCategories}
          customClasses={{
            button:
              "ma0 items-center flex pa1 dib items-center justify-center bn br1 panel--action relative bg-white bg-content--secondary bg-content--hover"
          }}
        />
      </li>
    );
  }

  const heroProps = {
    header: [
      {
        copy: "Custom Stripe Checkout and"
      },
      {
        classes: "color-primary underline",
        copy: "data"
      },
      {
        copy: "capture pages."
      }
    ],
    action: {
      tag: BUTTON_TAGS.LINK,
      href: blankDestination,
      copy: "Start",
      classes: {
        button:
          "fw6 br2 pa3 border-box input-reset ba bg-transparent pointer f4 dib link items-center flex flex-shrink-0 cell-grow"
      }
    },
    paddingBottom: 20,
    subheader: "Start building from scratch or use one of our templates."
  };

  return (
    <div className="mw8 center">
      <HeroBanner {...heroProps} />
      <div className="ph3">
        <div
          style={{ top: 56 }}
          className="w-100 sticky top-0 z-1 pv3 bb hairline-1 bg-white"
        >
          <SearchBar
            innerRef={inputRef}
            showReset
            customClasses={{ inputWrapper: "flex flex-row items-center pa0" }}
            placeholder="Search for a template"
            inputValue={search}
            valueSet={(val) => {
              const updatedValues = cloneDeep(values);
              updatedValues.search = val;
              setValues(updatedValues);
            }}
          />
          <ul className="mh0 mb0 mt3 pa0 flex flex-wrap">{categoryItems}</ul>
          <div className="pt1">
            <div
              className="link fw5 f6 link__decoration--hover color-primary pointer"
              onClick={() => {
                clearStorePageBuilder();
                fetchTemplates();
              }}
            >
              Reset templates
            </div>
          </div>
        </div>
        <div className="pv4 ph2">
          {loading ? (
            <CardDetailLoader />
          ) : (
            <PageTypePicker resetValues={resetValues} />
          )}
        </div>
      </div>
      <div className="pt5 hairline-1 bt">
        <Footer />
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppPagesTemplatesIndex
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(PageTypePickerContainer);
